<template>
  <div class="config-impressao">
    <div class="config-impressao__header">
      <h2 class="config-impressao__title">
        <i class="fas fa-print mr-2"></i>Configuração de Impressão
      </h2>
      <p class="config-impressao__subtitle">
        Defina a impressora padrão e as opções de impressão para este terminal.
      </p>
    </div>

    <!-- Alertas -->
    <div v-if="mensagemSucesso" class="config-impressao__alert config-impressao__alert--success">
      <i class="fas fa-check-circle mr-2"></i>{{ mensagemSucesso }}
    </div>
    <div v-if="mensagemErro" class="config-impressao__alert config-impressao__alert--error">
      <i class="fas fa-exclamation-circle mr-2"></i>{{ mensagemErro }}
    </div>

    <!-- Formulário -->
    <div class="config-impressao__card">
      <!-- Impressora Padrão -->
      <div class="config-impressao__field">
        <label class="config-impressao__label">
          Impressora padrão
          <button
            class="config-impressao__refresh-btn"
            :disabled="carregandoImpressoras"
            @click="carregarImpressoras"
            title="Recarregar lista de impressoras"
          >
            <i :class="carregandoImpressoras ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"></i>
          </button>
        </label>
        <select v-model="form.printer_name" class="config-impressao__select">
          <option value="">— Nenhuma (usar diálogo do sistema) —</option>
          <option
            v-for="p in impressoras"
            :key="p.name"
            :value="p.name"
          >
            {{ p.displayName || p.name }}{{ p.isDefault ? " (padrão do sistema)" : "" }}
          </option>
        </select>
        <small v-if="form.printer_name" class="config-impressao__hint">
          Nome técnico: <code>{{ form.printer_name }}</code>
        </small>
        <small v-if="!impressoras.length && !carregandoImpressoras" class="config-impressao__hint config-impressao__hint--warn">
          Nenhuma impressora encontrada. Verifique as impressoras instaladas no sistema.
        </small>
      </div>

      <!-- Tipo de impressão -->
      <div class="config-impressao__field">
        <label class="config-impressao__label">Tipo de impressão</label>
        <div class="config-impressao__radio-group">
          <label class="config-impressao__radio">
            <input type="radio" v-model="form.tipo" value="termica" />
            <span>Térmica (cupom)</span>
          </label>
          <label class="config-impressao__radio">
            <input type="radio" v-model="form.tipo" value="a4" />
            <span>Folha A4</span>
          </label>
        </div>
      </div>

      <!-- Largura do papel -->
      <div class="config-impressao__field">
        <label class="config-impressao__label">Largura do papel</label>
        <div class="config-impressao__radio-group">
          <label class="config-impressao__radio">
            <input type="radio" v-model.number="form.largura_mm" :value="58" />
            <span>58 mm</span>
          </label>
          <label class="config-impressao__radio">
            <input type="radio" v-model.number="form.largura_mm" :value="80" />
            <span>80 mm</span>
          </label>
          <label class="config-impressao__radio" v-if="form.tipo === 'a4'">
            <input type="radio" v-model.number="form.largura_mm" :value="210" />
            <span>A4 (210 mm)</span>
          </label>
        </div>
      </div>

      <!-- Imprimir automaticamente -->
      <div class="config-impressao__field">
        <label class="config-impressao__label">Imprimir automaticamente ao finalizar NFC-e</label>
        <div class="config-impressao__radio-group">
          <label class="config-impressao__radio">
            <input type="radio" v-model="form.imprimir_automatico" :value="true" />
            <span>Sim</span>
          </label>
          <label class="config-impressao__radio">
            <input type="radio" v-model="form.imprimir_automatico" :value="false" />
            <span>Não</span>
          </label>
        </div>
        <small class="config-impressao__hint">
          Quando ativo, o DANFE é impresso automaticamente após a autorização ou emissão em contingência.
        </small>
      </div>

      <!-- Abrir prévia antes de imprimir -->
      <div class="config-impressao__field">
        <label class="config-impressao__label">Abrir prévia antes de imprimir</label>
        <div class="config-impressao__radio-group">
          <label class="config-impressao__radio">
            <input type="radio" v-model="form.abrir_preview" :value="true" />
            <span>Sim</span>
          </label>
          <label class="config-impressao__radio">
            <input type="radio" v-model="form.abrir_preview" :value="false" />
            <span>Não (impressão silenciosa)</span>
          </label>
        </div>
        <small class="config-impressao__hint">
          Com prévia ativada, o sistema exibirá a janela de visualização antes de enviar para a impressora.
        </small>
      </div>

      <!-- Número de vias -->
      <div class="config-impressao__field config-impressao__field--inline">
        <label class="config-impressao__label">Número de vias</label>
        <input
          type="number"
          v-model.number="form.numero_vias"
          min="1"
          max="5"
          class="config-impressao__input-number"
        />
      </div>

      <!-- Ações -->
      <div class="config-impressao__actions">
        <button
          class="config-impressao__btn config-impressao__btn--secondary"
          @click="testarImpressao"
          :disabled="salvando || !form.printer_name"
          title="Imprime uma página de teste na impressora selecionada"
        >
          <i class="fas fa-vial mr-1"></i>Testar impressora
        </button>

        <button
          class="config-impressao__btn config-impressao__btn--primary"
          :disabled="salvando"
          @click="salvar"
        >
          <span v-if="salvando">
            <i class="fas fa-spinner fa-spin mr-1"></i>Salvando...
          </span>
          <span v-else>
            <i class="fas fa-save mr-1"></i>Salvar configuração
          </span>
        </button>
      </div>
    </div>

    <!-- Informação sobre impressão -->
    <div class="config-impressao__info">
      <i class="fas fa-info-circle mr-1"></i>
      A configuração de impressora é salva localmente neste terminal e não é sincronizada com o servidor.
    </div>
  </div>
</template>

<script>
import { getElectronAPI } from "@/plugins/desktop";

export default {
  name: "ConfiguracaoImpressao",

  data() {
    return {
      impressoras: [],
      carregandoImpressoras: false,
      salvando: false,
      mensagemSucesso: "",
      mensagemErro: "",

      form: {
        printer_name:         "",
        printer_display_name: "",
        printer_is_default:   false,
        printer_paper_width:  null,
        printer_tipo_papel:   "80mm",
        tipo:                 "termica",
        largura_mm:           80,
        imprimir_automatico:  true,
        abrir_preview:        false,
        numero_vias:          1,
      },
    };
  },

  async mounted() {
    await Promise.all([this.carregarImpressoras(), this.carregarConfigSalva()]);
  },

  methods: {
    async carregarImpressoras() {
      this.carregandoImpressoras = true;
      this.mensagemErro = "";
      try {
        const api = getElectronAPI();
        this.impressoras = await api.printer.listarImpressoras();
      } catch (e) {
        this.mensagemErro = `Erro ao listar impressoras: ${e.message || e}`;
      } finally {
        this.carregandoImpressoras = false;
      }
    },

    async carregarConfigSalva() {
      try {
        const api = getElectronAPI();
        const config = await api.impressoraConfig.buscar();
        if (config) {
          this.form.printer_name         = config.printer_name         ?? "";
          this.form.printer_display_name = config.printer_display_name ?? "";
          this.form.printer_is_default   = config.printer_is_default   ?? false;
          this.form.printer_paper_width  = config.printer_paper_width  ?? null;
          this.form.printer_tipo_papel   = config.printer_tipo_papel   ?? "80mm";
          this.form.tipo                 = config.tipo                 ?? "termica";
          this.form.largura_mm           = config.largura_mm           ?? 80;
          this.form.imprimir_automatico  = config.imprimir_automatico  ?? true;
          this.form.abrir_preview        = config.abrir_preview        ?? false;
          this.form.numero_vias          = config.numero_vias          ?? 1;
        }
      } catch (e) {
        // Configuração ainda não existe — usa defaults
      }
    },

    async salvar() {
      this.mensagemSucesso = "";
      this.mensagemErro = "";
      this.salvando = true;
      try {
        const api = getElectronAPI();

        // Atualiza o displayName com base na impressora selecionada
        const impressoraSelecionada = this.impressoras.find((p) => p.name === this.form.printer_name) || null;
        if (this.form.printer_name && !impressoraSelecionada) {
          throw new Error("A impressora configurada não foi encontrada. Verifique a configuração da impressora.");
        }

        const paperWidth = Number(
          impressoraSelecionada?.paperWidth
          ?? impressoraSelecionada?.options?.paperWidth
          ?? impressoraSelecionada?.options?.paper_width
          ?? impressoraSelecionada?.options?.width
          ?? 0
        ) || null;

        const larguraMm = this._inferirLarguraMm(paperWidth, this.form.largura_mm);

        const id_terminal_venda = parseInt(
          await api.config.get("id_terminal_venda") || "0",
          10
        ) || null;

        await api.impressoraConfig.salvar({
          ...this.form,
          printer_name: impressoraSelecionada?.name ?? this.form.printer_name,
          printer_display_name: impressoraSelecionada?.displayName ?? impressoraSelecionada?.name ?? this.form.printer_name,
          printer_is_default: !!impressoraSelecionada?.isDefault,
          printer_paper_width: paperWidth,
          largura_mm: larguraMm,
          printer_tipo_papel: larguraMm === 58 ? "58mm" : "80mm",
          id_terminal_venda,
        });

        this.mensagemSucesso = "Configuração salva com sucesso!";
        setTimeout(() => { this.mensagemSucesso = ""; }, 4000);
      } catch (e) {
        this.mensagemErro = `Erro ao salvar: ${e.message || e}`;
      } finally {
        this.salvando = false;
      }
    },

    async testarImpressao() {
      this.mensagemSucesso = "";
      this.mensagemErro = "";
      try {
        const api = getElectronAPI();
        const terminalName = await api.config.get("terminal_nome") || "Caixa do Ismael";
        const larguraMm = this._inferirLarguraMm(this.form.printer_paper_width, this.form.largura_mm);

        await api.printer.imprimirTeste({
          printerName: this.form.printer_name || null,
          terminalName,
          larguraMm,
        });
        this.mensagemSucesso = "Página de teste enviada para a impressora.";
        setTimeout(() => { this.mensagemSucesso = ""; }, 4000);
      } catch (e) {
        this.mensagemErro = `Erro no teste de impressão: ${e.message || e}`;
      }
    },

    _inferirLarguraMm(paperWidth, larguraAtual) {
      if ([58, 80].includes(Number(larguraAtual))) {
        return Number(larguraAtual);
      }
      if (!paperWidth) return 80;
      const mm = paperWidth > 200 ? Math.round(paperWidth / 1000) : Math.round(paperWidth);
      return mm <= 58 ? 58 : 80;
    },
  },
};
</script>

<style scoped>
.config-impressao {
  max-width: 640px;
  margin: 0 auto;
  padding: 24px 16px;
  color: #1e293b;
}

.config-impressao__header {
  margin-bottom: 24px;
}

.config-impressao__title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.config-impressao__subtitle {
  font-size: 13px;
  color: #64748b;
}

/* Alertas */
.config-impressao__alert {
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 16px;
}

.config-impressao__alert--success {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #86efac;
}

.config-impressao__alert--error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
}

/* Card principal */
.config-impressao__card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.config-impressao__field {
  margin-bottom: 22px;
}

.config-impressao__field--inline {
  display: flex;
  align-items: center;
  gap: 12px;
}

.config-impressao__field--inline .config-impressao__label {
  margin-bottom: 0;
  white-space: nowrap;
}

.config-impressao__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.config-impressao__select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  background: #f9fafb;
  color: #1f2937;
  outline: none;
  transition: border-color 0.15s;
}

.config-impressao__select:focus {
  border-color: #6366f1;
  background: #fff;
}

.config-impressao__input-number {
  width: 80px;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  text-align: center;
  background: #f9fafb;
}

.config-impressao__radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.config-impressao__radio {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
  color: #374151;
}

.config-impressao__radio input {
  cursor: pointer;
  accent-color: #6366f1;
}

.config-impressao__hint {
  display: block;
  font-size: 11px;
  color: #6b7280;
  margin-top: 5px;
}

.config-impressao__hint--warn {
  color: #b45309;
}

.config-impressao__hint code {
  background: #f3f4f6;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 11px;
}

.config-impressao__refresh-btn {
  background: none;
  border: none;
  color: #6366f1;
  cursor: pointer;
  padding: 0 4px;
  font-size: 12px;
}

.config-impressao__refresh-btn:disabled {
  color: #9ca3af;
  cursor: default;
}

/* Ações */
.config-impressao__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;
}

.config-impressao__btn {
  display: inline-flex;
  align-items: center;
  padding: 9px 18px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background 0.15s, opacity 0.15s;
}

.config-impressao__btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.config-impressao__btn--primary {
  background: #6366f1;
  color: #fff;
}

.config-impressao__btn--primary:not(:disabled):hover {
  background: #4f46e5;
}

.config-impressao__btn--secondary {
  background: #f1f5f9;
  color: #374151;
  border: 1px solid #e2e8f0;
}

.config-impressao__btn--secondary:not(:disabled):hover {
  background: #e2e8f0;
}

/* Info */
.config-impressao__info {
  margin-top: 16px;
  font-size: 11px;
  color: #94a3b8;
  text-align: center;
}
</style>
